// Run a callback whenever the app returns to the foreground.
//
// Native: @capacitor/app appStateChange(isActive). Web: visibilitychange to
// "visible". The app's QueryClient disables refetch-on-focus globally, so
// anything that must pick up server-side changes in a running app (billing
// mode, subscription tier) hooks in here. Mount once, globally (AppShell).

import { useEffect, useRef } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

export function useOnAppResume(callback: () => void) {
  // Latest callback without re-subscribing the native listener each render.
  const cb = useRef(callback);
  cb.current = callback;

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      let handle: { remove: () => Promise<void> } | null = null;
      let cancelled = false;
      void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) cb.current();
      }).then((h) => {
        if (cancelled) void h.remove();
        else handle = h;
      });
      return () => {
        cancelled = true;
        void handle?.remove();
      };
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") cb.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
}
